import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Grid, CircularProgress, Alert, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RiskSummaryDetailView from './RiskSummaryDetailView.js';
import OwnershipNetworkView from './OwnershipNetworkView.js';
import BusinessSummarySection from '../components/BusinessSummarySection.js';
import RiskSummaryText from '../components/RiskSummaryText.js';
import { BusinessProfileView } from './BusinessProfileView.js';
import Layout from '../components/Layout.tsx';
import ExternalApiResponseSection from '../components/ExternalApiResponseSection';
import { EditModeProvider, useEditMode } from '../components/EditMode/EditModeContext';
import SaveBar from '../components/EditMode/SaveBar';
import { RiskLevelEditor, AssessmentEditor, SummaryEditor } from '../components/EditMode/RiskEditControls';
import { AddUBOButton, AddEntityButton, RemoveEntityButton, RemoveUBOButton } from '../components/EditMode/UBOEditControls';
import EditHistory from '../components/EditHistory';
import ActionBar from '../components/EditMode/ActionBar';
import RunningVerificationPanel from '../components/RunningVerificationPanel';
import axios from '../auth/axiosConfig';

const EditModeToggle = () => {
  const { isEditMode, canEdit, isAdminTenant, isFrozen, enterEditMode, lockError, unfreezeVerification } = useEditMode();
  const [showUnfreezeDialog, setShowUnfreezeDialog] = useState(false);
  const [unfreezeReason, setUnfreezeReason] = useState('');
  const [unfreezeLoading, setUnfreezeLoading] = useState(false);
  const [unfreezeError, setUnfreezeError] = useState(null);

  const handleUnfreeze = async () => {
    setUnfreezeLoading(true);
    setUnfreezeError(null);
    try {
      await unfreezeVerification(unfreezeReason.trim());
      setShowUnfreezeDialog(false);
      setUnfreezeReason('');
    } catch (err) {
      setUnfreezeError(err.response?.data?.detail || 'Failed to unfreeze verification');
    } finally {
      setUnfreezeLoading(false);
    }
  };

  if (isFrozen && isAdminTenant) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="FROZEN" color="default" size="small" icon={<LockIcon />} sx={{ ml: 1 }} />
          <Button
            startIcon={<LockOpenIcon />}
            variant="outlined"
            size="small"
            onClick={() => setShowUnfreezeDialog(true)}
            sx={{ borderColor: '#ed6c02', color: '#ed6c02' }}
          >
            Unfreeze
          </Button>
        </Box>

        <Dialog open={showUnfreezeDialog} onClose={() => { setShowUnfreezeDialog(false); setUnfreezeError(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>Unfreeze Verification</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Provide a reason for unfreezing this verification. This will be recorded in the edit history.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Reason"
              value={unfreezeReason}
              onChange={(e) => setUnfreezeReason(e.target.value)}
              error={!!unfreezeError}
              helperText={unfreezeError}
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowUnfreezeDialog(false); setUnfreezeError(null); }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={unfreezeLoading || !unfreezeReason.trim()}
              onClick={handleUnfreeze}
            >
              {unfreezeLoading ? 'Unfreezing...' : 'Unfreeze'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (!canEdit) return null;

  return isEditMode ? (
    <Chip label="Editing" color="warning" size="small" sx={{ ml: 1 }} />
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        startIcon={<EditIcon />}
        variant="outlined"
        size="small"
        onClick={enterEditMode}
        sx={{ ml: 1 }}
      >
        Edit
      </Button>
      {lockError && (
        <Chip label={lockError} color="error" size="small" variant="outlined" />
      )}
    </Box>
  );
};

const VerificationDetailContent = ({ vrDetailData, setVrDetailData, id, businessProfileActiveTab, setBusinessProfileActiveTab }) => {
  // canEdit gates EditHistory (visible whenever admin is in edit mode, even
  // on PENDING/RUNNING). canEditData gates inline editors + the save flow,
  // and is only true on RFR/COMPLETED — i.e. statuses the backend's
  // PATCH /detail-data actually accepts.
  const { canEdit, canEditData } = useEditMode();
  const handleRiskSummaryUpdate = (updatedOrFn) => {
    setVrDetailData(prev => {
      const updatedRiskSummary = typeof updatedOrFn === 'function'
        ? updatedOrFn(prev.risk_summary)
        : updatedOrFn;
      const newData = { ...prev, risk_summary: updatedRiskSummary };
      const trivial = updatedRiskSummary.find(s => s.score_type === 'TRIVIAL');
      if (trivial) {
        newData.request = { ...prev.request, score: trivial.score };
      }
      return newData;
    });
  };

  const handleSummaryUpdate = (newSummary) => {
    setVrDetailData(prev => ({ ...prev, human_readable_summary: newSummary }));
  };

  const handleUbosUpdate = (updatedUbos) => {
    setVrDetailData(prev => ({ ...prev, ubos: updatedUbos }));
  };

  const handleGraphUpdate = (updatedGraph) => {
    setVrDetailData(prev => ({ ...prev, graph_data: updatedGraph }));
  };

  const handleCombinedUpdate = (mutator) => {
    setVrDetailData(prev => {
      const updates = mutator(prev);
      return { ...prev, ...updates };
    });
  };

  const handleAddressesUpdate = (updatedAddresses) => {
    setVrDetailData(prev => {
      // Find which addresses were removed and clean them from the graph
      const prevIds = new Set((prev.company_details?.addresses || []).map(a => a.id));
      const newIds = new Set(updatedAddresses.map(a => a.id));
      const removedIds = new Set([...prevIds].filter(id => !newIds.has(id)));

      let graphData = prev.graph_data;
      if (removedIds.size > 0 && graphData) {
        // Remove address nodes (id = addr_<addressId> or just the id itself)
        const removedNodeIds = new Set();
        for (const rid of removedIds) {
          removedNodeIds.add(rid);
          removedNodeIds.add(`addr_${rid}`);
        }
        // Also match by label (address value)
        const removedValues = new Set(
          (prev.company_details?.addresses || [])
            .filter(a => removedIds.has(a.id))
            .map(a => a.value)
        );

        graphData = {
          ...graphData,
          nodes: (graphData.nodes || []).filter(n => {
            if (removedNodeIds.has(n.id)) return false;
            if (n.type === 'ADDRESS' && removedValues.has(n.label)) return false;
            return true;
          }),
          edges: (graphData.edges || []).filter(e => {
            const s = String(e.source?.id || e.source);
            const t = String(e.target?.id || e.target);
            return ![...removedNodeIds].some(rid => s === rid || t === rid) &&
              !(graphData.nodes || []).some(n => n.type === 'ADDRESS' && removedValues.has(n.label) && (s === n.id || t === n.id));
          }),
        };
      }

      return {
        ...prev,
        company_details: { ...prev.company_details, addresses: updatedAddresses },
        ...(graphData !== prev.graph_data ? { graph_data: graphData } : {}),
      };
    });
  };

  const handleWebPresenceUpdate = (updatedWebPresence) => {
    setVrDetailData(prev => ({
      ...prev,
      company_details: { ...prev.company_details, web_presence: updatedWebPresence },
    }));
  };

  const handleEmailsUpdate = (updatedEmails) => {
    setVrDetailData(prev => ({
      ...prev,
      company_details: { ...prev.company_details, emails: updatedEmails },
    }));
  };

  const handleEntityUpdate = (updatedEntity) => {
    setVrDetailData(prev => ({ ...prev, entity: updatedEntity, request: { ...prev.request, business_name: updatedEntity.legal_name } }));
  };

  const handleLiensUpdate = (updatedLiens) => {
    setVrDetailData(prev => ({ ...prev, liens: updatedLiens }));
  };

  const handleLitigationsUpdate = (updatedLitigations) => {
    setVrDetailData(prev => ({ ...prev, litigations: updatedLitigations }));
  };

  const handlePhonesUpdate = (updatedPhones) => {
    setVrDetailData(prev => ({
      ...prev,
      company_details: { ...prev.company_details, phones: updatedPhones },
    }));
  };

  const graphKey = useMemo(() => {
    const nodes = vrDetailData.graph_data?.nodes;
    if (!nodes) return 'no-graph';
    return nodes.map(n => n.id + n.label).join(',');
  }, [vrDetailData.graph_data?.nodes]);

  return (
    <Box sx={{ pt: 1, px: 3, pb: 3, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <EditModeToggle />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BusinessSummarySection request={vrDetailData.request} />
        </Grid>

        <Grid item xs={12}>
          <RiskSummaryText
            summary={vrDetailData.human_readable_summary}
            riskSummary={vrDetailData.risk_summary}
          />
          {canEditData && <SummaryEditor summary={vrDetailData.human_readable_summary} onUpdate={handleSummaryUpdate} />}
          {canEditData && <RiskLevelEditor riskSummary={vrDetailData.risk_summary} onUpdate={handleRiskSummaryUpdate} />}
          {canEditData && <AssessmentEditor riskSummary={vrDetailData.risk_summary} onUpdate={handleRiskSummaryUpdate} />}
          <RiskSummaryDetailView
            risk_summary={vrDetailData.risk_summary}
            title="Risk rationale"
            onNavigateToTab={setBusinessProfileActiveTab}
            verificationId={id}
            onUpdateRiskSummary={handleRiskSummaryUpdate}
          />
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <BusinessProfileView
                company_details={vrDetailData.company_details}
                entity={vrDetailData.entity}
                ubos={vrDetailData.ubos}
                liens={vrDetailData.liens}
                litigations={vrDetailData.litigations}
                branch_data={vrDetailData.branch_data}
                title="Business profile information"
                activeTab={businessProfileActiveTab}
                onTabChange={setBusinessProfileActiveTab}
                graphData={vrDetailData.graph_data}
                onUpdateUbos={canEditData ? handleUbosUpdate : undefined}
                onUpdateGraph={canEditData ? handleGraphUpdate : undefined}
                onCombinedUpdate={canEditData ? handleCombinedUpdate : undefined}
                onUpdateAddresses={canEditData ? handleAddressesUpdate : undefined}
                onUpdateWebPresence={canEditData ? handleWebPresenceUpdate : undefined}
                onUpdatePhones={canEditData ? handlePhonesUpdate : undefined}
                onUpdateEmails={canEditData ? handleEmailsUpdate : undefined}
                onUpdateLiens={canEditData ? handleLiensUpdate : undefined}
                onUpdateLitigations={canEditData ? handleLitigationsUpdate : undefined}
                onUpdateEntity={canEditData ? handleEntityUpdate : undefined}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <OwnershipNetworkView
              key={graphKey}
              verificationId={id}
              graphData={vrDetailData.graph_data}
              entity={vrDetailData.entity}
              ubos={vrDetailData.ubos}
              title="Ownership network"
              onUpdateGraph={canEditData ? handleGraphUpdate : undefined}
              onCombinedUpdate={canEditData ? handleCombinedUpdate : undefined}
            />
          </Grid>
          {canEdit && (
            <Grid item xs={12}>
              <EditHistory verificationId={id} onDataRestored={(data) => setVrDetailData(prev => ({
                ...data,
                _status: prev?._status,
                _is_frozen: prev?._is_frozen,
              }))} />
            </Grid>
          )}
          {vrDetailData.external_api_response && (
            <Grid item xs={12}>
              <ExternalApiResponseSection data={vrDetailData.external_api_response} />
            </Grid>
          )}
        </Grid>
      </Grid>
      <SaveBar />
    </Box>
  );
};

const VerificationDetailView = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vrDetailData, setVrDetailData] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [businessProfileActiveTab, setBusinessProfileActiveTab] = useState(0);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    if (!silent) setError(null);

    try {
      const response = await axios.get(`/ui/${id}/detailed`, {
        withCredentials: true
      });

      setVrDetailData(response.data);
      setVerificationStatus((response.data._status || '').toUpperCase());
      setIsFrozen(response.data._is_frozen || false);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || err.message || 'Failed to load verification data');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {

    loadData();
  }, [id]);

  useEffect(() => {
    if (verificationStatus !== 'PENDING' && verificationStatus !== 'RUNNING') return undefined;
    const intervalId = setInterval(() => loadData({ silent: true }), 5000);
    return () => clearInterval(intervalId);
  }, [verificationStatus, loadData]);

  const handleDataUpdate = useCallback((newData) => {
    setVrDetailData(newData);
  }, []);

  if (isLoading) {
    return (
      <Layout title="Business Details">
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Business Details">
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </Layout>
    );
  }

  if (vrDetailData._no_detail_data) {
    return (
      <Layout
        title={`Business Details / ${vrDetailData.request?.business_name || 'Unnamed'}`}
        showBackButton={true}
      >
        <RunningVerificationPanel
          verificationId={id}
          vrDetailData={vrDetailData}
          onRefresh={loadData}
        />
      </Layout>
    );
  }

  return (
    <EditModeProvider
      verificationId={id}
      verificationStatus={verificationStatus}
      isFrozen={isFrozen}
      detailData={vrDetailData}
      onDataUpdate={handleDataUpdate}
      onFrozenChange={setIsFrozen}
    >
      <Layout
        title={`Business Details / ${vrDetailData.request.business_name}`}
        showBackButton={true}
      >
        <ActionBar
          verificationId={id}
          verificationStatus={verificationStatus}
          onStatusChange={(newStatus) => setVerificationStatus(newStatus)}
          onRefresh={loadData}
        />
        <VerificationDetailContent
          vrDetailData={vrDetailData}
          setVrDetailData={setVrDetailData}
          id={id}
          businessProfileActiveTab={businessProfileActiveTab}
          setBusinessProfileActiveTab={setBusinessProfileActiveTab}
        />
      </Layout>
    </EditModeProvider>
  );
};

export default VerificationDetailView;
